import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar, Monitor, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { fetchUserSessions } from '../../../services/analyticsService';
import { fetchUserRecommendations } from '../../../services/recommendationService';

export default function UserSessionModal({ user, onClose, onRefresh }) {
  const [sessions, setSessions] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [activeTab, setActiveTab] = useState('sessions');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUserData();
  }, [user?.userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [sessionsData, recommendationsData] = await Promise.all([
        fetchUserSessions(user?.userId, 100),
        fetchUserRecommendations(user?.userId, 50)
      ]);

      setSessions(sessionsData);
      setRecommendations(recommendationsData);
    } catch (err) {
      console.error('Error loading user data:', err);
      setError(err?.message || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getSessionStatus = (session) => {
    if (session?.signOutAt) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Completed</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Active</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2">{user?.fullName}</h2>
              <p className="text-blue-100">{user?.email}</p>
              {user?.institution && (
                <p className="text-blue-100 text-sm mt-1">{user?.institution} • {user?.specialty}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-blue-500 bg-opacity-30 rounded-lg p-3">
              <div className="text-blue-100 text-sm">Total Sessions</div>
              <div className="text-2xl font-bold">{user?.totalSessions}</div>
            </div>
            <div className="bg-blue-500 bg-opacity-30 rounded-lg p-3">
              <div className="text-blue-100 text-sm">Total Usage</div>
              <div className="text-2xl font-bold">{formatDuration(user?.totalUsageMinutes)}</div>
            </div>
            <div className="bg-blue-500 bg-opacity-30 rounded-lg p-3">
              <div className="text-blue-100 text-sm">AI Queries</div>
              <div className="text-2xl font-bold">{user?.totalRecommendations}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('sessions')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'sessions' ?'border-b-2 border-blue-600 text-blue-600' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              Session History ({sessions?.length})
            </button>
            <button
              onClick={() => setActiveTab('recommendations')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'recommendations' ?'border-b-2 border-blue-600 text-blue-600' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              AI Recommendation History ({recommendations?.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 300px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          ) : activeTab === 'sessions' ? (
            <div className="space-y-3">
              {sessions?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No session history available</p>
              ) : (
                sessions?.map((session) => (
                  <div key={session?.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Monitor className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-900">Session</span>
                      </div>
                      {getSessionStatus(session)}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Calendar className="w-4 h-4" />
                          <span>Sign In</span>
                        </div>
                        <div className="font-medium text-gray-900">{formatDate(session?.signInAt)}</div>
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Calendar className="w-4 h-4" />
                          <span>Sign Out</span>
                        </div>
                        <div className="font-medium text-gray-900">{formatDate(session?.signOutAt)}</div>
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Clock className="w-4 h-4" />
                          <span>Duration</span>
                        </div>
                        <div className="font-medium text-blue-600">{formatDuration(session?.sessionDurationMinutes)}</div>
                      </div>
                      
                      {session?.ipAddress && (
                        <div>
                          <div className="flex items-center gap-2 text-gray-600 mb-1">
                            <Globe className="w-4 h-4" />
                            <span>IP Address</span>
                          </div>
                          <div className="font-medium text-gray-900">{session?.ipAddress}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {recommendations?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No AI recommendation history available</p>
              ) : (
                recommendations?.map((rec) => (
                  <div key={rec?.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-medium text-gray-900 mb-1">
                          {rec?.bodyRegion} • {rec?.patientGender}, {rec?.patientAge} years
                        </div>
                        <div className="text-sm text-gray-600">{formatDate(rec?.createdAt)}</div>
                      </div>
                      {rec?.confidenceScore && (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                          {Math.round(rec?.confidenceScore * 100)}% confidence
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Clinical Presentation:</span>
                        <p className="text-gray-600 mt-1">{rec?.clinicalPresentation}</p>
                      </div>
                      
                      {rec?.preferredModalities && rec?.preferredModalities?.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-700">Preferred Modalities:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {rec?.preferredModalities?.map((modality, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                {modality}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <span className="font-medium text-gray-700">AI Recommendation:</span>
                        <p className="text-gray-600 mt-1 whitespace-pre-wrap">{rec?.recommendationText}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}